import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface Webhook {
  id: string;
  url: string;
  events: string[];
  secret: string;
  status: 'active' | 'inactive' | 'failed';
  createdBy: string;
  lastTriggered?: Date;
  successCount: number;
  failureCount: number;
  createdAt: Date;
}

const AdminWebhooksPage: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    setLoading(true);
    try {
      // Mock data
      const mockData: Webhook[] = [
        {
          id: '1',
          url: 'https://api.example.com/webhooks/payments',
          events: ['payment.success', 'payment.failed', 'subscription.created'],
          secret: 'whsec_*********************',
          status: 'active',
          createdBy: 'admin@example.com',
          lastTriggered: new Date(Date.now() - 3600000),
          successCount: 1245,
          failureCount: 3,
          createdAt: new Date('2024-01-15'),
        },
        {
          id: '2',
          url: 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXX',
          events: ['report.created', 'user.suspended'],
          secret: 'whsec_*********************',
          status: 'active',
          createdBy: 'admin@example.com',
          lastTriggered: new Date(Date.now() - 7200000),
          successCount: 89,
          failureCount: 0,
          createdAt: new Date('2024-02-20'),
        },
        {
          id: '3',
          url: 'https://api.disabled-service.com/webhook',
          events: ['post.published'],
          secret: 'whsec_*********************',
          status: 'failed',
          createdBy: 'admin@example.com',
          lastTriggered: new Date(Date.now() - 86400000),
          successCount: 45,
          failureCount: 12,
          createdAt: new Date('2024-03-10'),
        },
      ];
      setWebhooks(mockData);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === 'active') return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
    if (status === 'failed') return <XCircleIcon className="w-5 h-5 text-red-600" />;
    return <div className="w-5 h-5 rounded-full bg-gray-400" />;
  };

  const handleTest = async (id: string) => {
    console.log('Testing webhook:', id);
    // Implement test logic
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;
    console.log('Deleting webhook:', id);
    // Implement deletion logic
  };

  const filteredWebhooks = webhooks.filter(
    (webhook) =>
      webhook.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.events.some((e) => e.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const stats = {
    total: webhooks.length,
    active: webhooks.filter((w) => w.status === 'active').length,
    failed: webhooks.filter((w) => w.status === 'failed').length,
    totalCalls: webhooks.reduce((sum, w) => sum + w.successCount + w.failureCount, 0),
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">Loading webhooks...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Webhooks</h1>
          <p className="mt-2 text-gray-600">Manage webhook integrations for external services</p>
        </div>
        <Button>
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Webhook
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Webhooks</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Active</p>
            <p className="text-3xl font-bold text-green-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Failed</p>
            <p className="text-3xl font-bold text-red-600">{stats.failed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Calls</p>
            <p className="text-3xl font-bold text-blue-600">{stats.totalCalls}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by URL or event type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Webhooks List */}
      <div className="space-y-4">
        {filteredWebhooks.map((webhook) => (
          <Card key={webhook.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  {getStatusIcon(webhook.status)}
                  <div>
                    <p className="text-sm font-medium text-gray-900 break-all">{webhook.url}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Created by {webhook.createdBy} on{' '}
                      {new Date(webhook.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    webhook.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : webhook.status === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {webhook.status}
                </span>
              </div>
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Events:</p>
                <div className="flex flex-wrap gap-2">
                  {webhook.events.map((event, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      {event}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-600">Success</p>
                  <p className="text-lg font-bold text-green-600">{webhook.successCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Failures</p>
                  <p className="text-lg font-bold text-red-600">{webhook.failureCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Last Triggered</p>
                  <p className="text-xs text-gray-900">
                    {webhook.lastTriggered
                      ? new Date(webhook.lastTriggered).toLocaleString()
                      : 'Never'}
                  </p>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-500 font-mono">{webhook.secret}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTest(webhook.id)}>
                    <ArrowPathIcon className="w-4 h-4 mr-1" />
                    Test
                  </Button>
                  <Button variant="outline" size="sm">
                    Edit
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(webhook.id)}>
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminWebhooksPage;
